import {
  HOMEWORK_MATERIAL_MAX,
  HOMEWORK_TASK_TEXT_MAX,
  heavyMedia,
  linkify,
  type HomeworkMaterialMediaRef,
  type MediaFile,
} from "@edu/shared";
import { megabytesText } from "../../media/duration-text.ts";
import type { ViewerImage } from "../../image-viewer/viewer-model.ts";
import { imageLabel, type ViewerMediaKind } from "../../image-viewer/viewer-text.ts";
import { audioCountText, charCountText, imageCountText, videoCountText } from "../../../lib/plurals.ts";

/**
 * Материал домашки в форме выдачи: текст задания и картинки (1.1-1.2
 * `docs/homework-scratch-design.md`). Чистые функции без React, чтобы правила
 * «что уедет на сервер» и «можно ли нажимать „Задать“» проверялись тестом.
 *
 * ⚠️ Картинки в форме это ОДИН упорядоченный список, откуда бы они ни пришли:
 * загрузка с устройства, доска урока, заготовка, «Задать ещё раз» или
 * продолжение раздачи. Флажок у ленты урока и заготовки это членство файла в
 * этом списке, а не второй список рядом: иначе порядок показа у ученика
 * пришлось бы собирать из трёх мест.
 *
 * ⚠️ С 16.10.2026 в том же списке аудио и видео из Материалов
 * (`docs/homework-media-plan.md`, Р1 и Р3): порядок и потолок у всех видов
 * общие, отличается только миниатюра (`media`).
 */

/** Чем рисовать миниатюру. Подпись ссылки живёт минуты, поэтому путь не главное. */
export type MaterialThumb =
  /** Ужатые байты только что загруженной картинки, адрес `URL.createObjectURL`. */
  | { kind: "blob"; url: string }
  /** Подписанный путь из ленты урока или заготовки. */
  | { kind: "path"; path: string }
  /** Известен только файл: миниатюра берёт ссылку сама (продолжение раздачи). */
  | { kind: "file" }
  /**
   * Аудио или видео из Материалов (Р6 `docs/homework-media-plan.md`): плитка со
   * значком и именем, без `<img>`. Вес `null`, если он неизвестен (повтор с
   * карточки, где его не было): тогда форма просто молчит о тяжести.
   *
   * `posterPath` это подписанный путь кадра-обложки видео (Р4
   * `docs/homework-media-form-player-plan.md`): из Материалов и с карточки
   * при «Задать ещё раз». У плана раздачи и у файла без превью `null`, и
   * плитка рисует значок. Живёт минуты, поэтому в план раздачи не едет.
   */
  | {
      kind: "media";
      fileKind: "audio" | "video";
      name: string;
      durationMs: number | null;
      sizeBytes: number | null;
      posterPath: string | null;
    };

export interface MaterialItem {
  /** Ключ строки: у загрузки файла ещё нет, а строка на экране уже есть. */
  key: string;
  fileId: string | null;
  status: "uploading" | "ready" | "failed";
  /** Доля отправленного, от 0 до 1. Есть смысл только у `uploading`. */
  progress: number;
  error: string | null;
  thumb: MaterialThumb;
}

export function readyItem(fileId: string, thumb: MaterialThumb): MaterialItem {
  return { key: fileId, fileId, status: "ready", progress: 1, error: null, thumb };
}

/**
 * Сколько картинок ещё можно добавить. Считаются ВСЕ строки, включая идущие и
 * упавшие загрузки: иначе пять одновременно выбранных фото обходили бы потолок.
 */
export function roomLeft(items: readonly MaterialItem[]): number {
  return Math.max(0, HOMEWORK_MATERIAL_MAX - items.length);
}

export function hasFile(items: readonly MaterialItem[], fileId: string): boolean {
  return items.some((it) => it.fileId === fileId);
}

/**
 * Флажок у картинки урока или заготовки: снять убирает файл из списка, поставить
 * дописывает в конец. Упор в потолок это «ничего не менять», а не отказ.
 */
export function toggleFile(
  items: readonly MaterialItem[],
  fileId: string,
  thumb: MaterialThumb,
): MaterialItem[] {
  if (hasFile(items, fileId)) return items.filter((it) => it.fileId !== fileId);
  if (roomLeft(items) === 0) return [...items];
  return [...items, readyItem(fileId, thumb)];
}

/** «Отметить все»: дописывает недостающие по порядку, пока есть место. */
export function addFiles(
  items: readonly MaterialItem[],
  files: readonly { fileId: string; thumb: MaterialThumb }[],
): MaterialItem[] {
  const out = [...items];
  for (const f of files) {
    if (out.length >= HOMEWORK_MATERIAL_MAX) break;
    if (!hasFile(out, f.fileId)) out.push(readyItem(f.fileId, f.thumb));
  }
  return out;
}

/**
 * Файл из Материалов (кнопка «Из Материалов»): картинка, аудио или видео встают
 * в конец ОБЩЕГО списка. ⚠️ Потолок один на все виды (Р1 плана), поэтому упор в
 * него это «ничего не менять», как у флажка, а уже добавленный файл второй раз
 * не встаёт: на сервере пара `(домашка, файл)` уникальна.
 */
export function addMediaFile(
  items: readonly MaterialItem[],
  fileId: string,
  thumb: MaterialThumb,
): MaterialItem[] {
  if (hasFile(items, fileId) || roomLeft(items) === 0) return [...items];
  return [...items, readyItem(fileId, thumb)];
}

/**
 * Миниатюра файла, выбранного в Материалах. Картинка остаётся картинкой (В3
 * плана): у ученика она встаёт среди картинок материала, как с доски урока.
 * Документ материалом не бывает, `null`.
 */
export function thumbOfMediaFile(file: MediaFile): MaterialThumb | null {
  if (file.kind === "image") {
    return file.previewPath === null ? { kind: "file" } : { kind: "path", path: file.previewPath };
  }
  if (file.kind === "doc") return null;
  return {
    kind: "media",
    fileKind: file.kind,
    name: file.name,
    durationMs: file.durationMs,
    sizeBytes: file.sizeBytes,
    posterPath: file.previewPath,
  };
}

/** Слово вида медиа: им называется файл без имени. */
export function mediaKindWord(kind: ViewerMediaKind): string {
  return kind === "video" ? "Видео" : "Аудио";
}

/** Расширение файла до пяти знаков: его у машинного имени не считаем. */
const EXTENSION = /\.[0-9a-z]{1,5}$/i;
/** Основа машинного имени: только шестнадцатеричные знаки, дефисы и подчёркивания. */
const MACHINE_BASE = /^[0-9a-f_-]+$/i;
/** Сколько шестнадцатеричных знаков нужно, чтобы основа была идентификатором, а не словом. */
const MACHINE_MIN_HEX = 8;

/**
 * Имя, которое дал файлу сервер или программа, а не человек: uuid, хеш,
 * «414181208-44100-2-8277e0229c943». Признак: в основе без расширения только
 * цифры, латинские a-f, дефисы и подчёркивания, таких знаков не меньше восьми,
 * и среди них есть и цифра, и буква a-f. Без цифры именем остаются «cafe.mp3» и
 * «deadbeef.mp3», без буквы даты и номера («12-05-2024.mp3», «20241012.mp3»),
 * без порога длины «12.mp3» и «3-a.mp3».
 */
function isMachineName(name: string): boolean {
  const base = name.replace(EXTENSION, "");
  if (!MACHINE_BASE.test(base) || !/[0-9]/.test(base) || !/[a-f]/i.test(base)) return false;
  return base.replace(/[_-]/g, "").length >= MACHINE_MIN_HEX;
}

/**
 * Имя медиа, которое стоит показать (Р5 плана). `null` у пустого и у машинного
 * имени (`isMachineName`): преподавателю uuid или хеш не говорит ничего, и тогда
 * файл называется словом вида.
 */
export function mediaShownName(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed === "" || isMachineName(trimmed)) return null;
  return trimmed;
}

/**
 * Имя медиа для читалки, слоя и строки веса: «Видео «Урок 5.mp4»». Без имени,
 * или когда имя это само слово вида («видео.mp4», так «Задать ещё раз»
 * подставляет имя, которого нет), просто «Видео»: «Видео «Видео»» не бывает.
 */
export function mediaLabel(kind: ViewerMediaKind, name: string): string {
  const word = mediaKindWord(kind);
  const shown = mediaShownName(name);
  if (shown === null) return word;
  const base = shown.replace(EXTENSION, "").trim();
  return base.toLowerCase() === word.toLowerCase() ? word : `${word} «${shown}»`;
}

/** Откуда слой просмотра берёт картинку строки формы. */
export type ViewerSource = { kind: "blob"; url: string } | { kind: "file"; fileId: string };

/**
 * Файлы формы для просмотра крупно (Р3 `docs/homework-image-viewer-plan.md`),
 * в порядке списка: картинки, а с 17.10.2026 и видео с аудио своими слайдами
 * (Р1 и Р3 `docs/homework-media-form-player-plan.md`). Медиа берётся из файла,
 * кадр видео из `posters` по ключу строки (адрес разрешает вызывающий).
 *
 * ⚠️ Вид `path` это путь КАДРА превью из ленты урока или заготовки, то есть
 * маленькая картинка: крупно показывается сам файл через его `fileId`, а не
 * кадр. `blob` берётся как есть и у идущей или упавшей загрузки: ужатые байты
 * уже на экране. Строка без `blob` и без `fileId` (файл ещё ужимается) в
 * карусель не входит, и кнопки открыть у неё нет.
 *
 * ⚠️ Номера в `alt` только у списка из одних картинок («Картинка 2 из 5»).
 * Счётчик слоя считает все слайды, и в смешанном списке номер среди картинок
 * разошёлся бы с ним: там картинка просто «Картинка», позицию называет счётчик.
 */
export function viewerImagesOf(
  items: readonly MaterialItem[],
  posters: ReadonlyMap<string, string> = new Map(),
): { image: ViewerImage; source: ViewerSource }[] {
  const entries = items.flatMap((it): { item: MaterialItem; source: ViewerSource }[] => {
    if (it.thumb.kind === "blob") return [{ item: it, source: { kind: "blob", url: it.thumb.url } }];
    return it.fileId === null ? [] : [{ item: it, source: { kind: "file", fileId: it.fileId } }];
  });
  const pictures = entries.filter(({ item }) => item.thumb.kind !== "media");
  const mixed = pictures.length < entries.length;
  return entries.map(({ item, source }) => {
    const { key, thumb } = item;
    if (thumb.kind === "media") {
      const label = mediaLabel(thumb.fileKind, thumb.name);
      const media = { kind: thumb.fileKind, poster: posters.get(key) ?? null, title: label };
      return { image: { key, alt: label, width: null, height: null, media }, source };
    }
    const alt = mixed ? "Картинка" : imageLabel(pictures.findIndex((p) => p.item === item), pictures.length);
    return { image: { key, alt, width: null, height: null }, source };
  });
}

/** Подписи к аудио и видео списка для плана раздачи, в порядке показа. */
export function materialMediaRefs(items: readonly MaterialItem[]): HomeworkMaterialMediaRef[] {
  return items.flatMap((it) => {
    if (it.status !== "ready" || it.fileId === null || it.thumb.kind !== "media") return [];
    const ref: HomeworkMaterialMediaRef = {
      fileId: it.fileId,
      kind: it.thumb.fileKind,
      name: it.thumb.name,
      durationMs: it.thumb.durationMs,
    };
    return [it.thumb.sizeBytes === null ? ref : { ...ref, sizeBytes: it.thumb.sizeBytes }];
  });
}

/**
 * Миниатюры для «Задать ещё раз» и продолжения раздачи. Порядок задают файлы;
 * у аудио и видео миниатюра из подписи, остальные остаются картинками, адрес к
 * которым миниатюра берёт сама. Кадры видео (`posters`, по `fileId`) есть
 * только у «Задать ещё раз»: в подписи и в плане раздачи их нет.
 */
export function restoreThumbs(
  fileIds: readonly string[],
  media: readonly HomeworkMaterialMediaRef[],
  posters: Readonly<Record<string, string>> = {},
): { fileId: string; thumb: MaterialThumb }[] {
  const byId = new Map(media.map((m) => [m.fileId, m]));
  return fileIds.map((fileId) => {
    const m = byId.get(fileId);
    if (m === undefined) return { fileId, thumb: { kind: "file" } };
    return {
      fileId,
      thumb: {
        kind: "media",
        fileKind: m.kind,
        name: m.name,
        durationMs: m.durationMs ?? null,
        sizeBytes: m.sizeBytes ?? null,
        posterPath: Object.hasOwn(posters, fileId) ? (posters[fileId] ?? null) : null,
      },
    };
  });
}

/** Вес тяжёлого файла плашкой на плитке, «4 МБ». `null` у лёгкого и у неизвестного веса. */
export function heavyMediaSize(thumb: MaterialThumb): string | null {
  if (thumb.kind !== "media" || thumb.sizeBytes === null) return null;
  if (!heavyMedia({ sizeBytes: thumb.sizeBytes, durationMs: thumb.durationMs })) return null;
  return megabytesText(thumb.sizeBytes);
}

/**
 * Строка о тяжёлом файле под рядом плиток (Р3 `docs/homework-media-plan.md`,
 * Р7 `docs/homework-media-form-player-plan.md`): «Видео «Имя» весит 4 МБ.».
 * `null` у лёгкого и у файла с неизвестным весом. Файл назван так же, как
 * читалка называет плитку: строк бывает несколько, и «Видео весит» не говорит,
 * какое. Почему это важно, говорит `heavyMediaNotes` одной фразой на все.
 */
export function heavyMediaText(thumb: MaterialThumb): string | null {
  const size = heavyMediaSize(thumb);
  if (thumb.kind !== "media" || size === null) return null;
  return `${mediaLabel(thumb.fileKind, thumb.name)} весит ${size}.`;
}

/** «Видео» и «аудио» оба среднего рода, поэтому у одного файла всегда «его». */
const HEAVY_WHY_ONE = "По мобильному интернету ученик может его не дождаться.";
const HEAVY_WHY_MANY = "По мобильному интернету ученик может их не дождаться.";

/**
 * Предупреждение о тяжёлых файлах ряда. Не запрет: учеников преподаватель знает
 * лучше нас. У одного файла причина в той же строке; у нескольких она общей
 * фразой `why` после списка, а не повтором в каждой строке.
 */
export function heavyMediaNotes(items: readonly MaterialItem[]): {
  lines: { key: string; text: string }[];
  why: string | null;
} {
  const lines = items.flatMap((item) => {
    const text = heavyMediaText(item.thumb);
    return text === null ? [] : [{ key: item.key, text }];
  });
  const [only] = lines;
  if (lines.length === 1 && only !== undefined) {
    return { lines: [{ key: only.key, text: `${only.text} ${HEAVY_WHY_ONE}` }], why: null };
  }
  return { lines, why: lines.length === 0 ? null : HEAVY_WHY_MANY };
}

/** Длительность плашкой на плитке, «4:05». */
export function durationText(ms: number): string {
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/**
 * Сколько файлов из «Отметить все» не поместилось в потолок: недостающие до
 * вызова минус добавленные. Уже отмеченные в счёт не идут.
 */
export function overflowOf(
  before: readonly MaterialItem[],
  files: readonly { fileId: string }[],
  after: readonly MaterialItem[],
): number {
  const missing = files.filter((f) => !hasFile(before, f.fileId)).length;
  return Math.max(0, missing - (after.length - before.length));
}

/** Слова про не поместившиеся картинки. `null`, если поместились все. */
export function overflowText(count: number): string | null {
  if (count <= 0) return null;
  return `Не поместилось картинок: ${count}. К одной домашке можно приложить до ${HOMEWORK_MATERIAL_MAX}.`;
}

/** Файлы, которые уедут на сервер, в порядке показа. Только загруженные. */
export function materialFileIds(items: readonly MaterialItem[]): string[] {
  return items.flatMap((it) => (it.status === "ready" && it.fileId !== null ? [it.fileId] : []));
}

/**
 * Что мешает нажать «Задать» со стороны материала. `null` значит «ничего».
 *
 * ⚠️ Упавшая загрузка тоже держит кнопку: картинка на экране есть, а на сервер
 * она бы не уехала, и ученик получил бы домашку без того, что преподаватель
 * видел в форме. Лечится повтором или крестиком, и оба стоят у самой картинки.
 */
export function materialBlock(items: readonly MaterialItem[]): "uploading" | "failed" | null {
  if (items.some((it) => it.status === "uploading")) return "uploading";
  if (items.some((it) => it.status === "failed")) return "failed";
  return null;
}

/** Текст задания по существу: одни пробелы сервер записывает как его отсутствие. */
export function hasTaskText(text: string): boolean {
  return text.trim() !== "";
}

/**
 * Счётчик знаков у поля текста задания: только у приближения к потолку.
 * Постоянный счётчик под большим полем это шум на каждом открытии формы.
 */
export const TASK_TEXT_WARN_AT = 1_000;

export function taskTextCounter(length: number): string | null {
  const left = HOMEWORK_TASK_TEXT_MAX - length;
  if (left > TASK_TEXT_WARN_AT) return null;
  return left <= 0 ? "Больше текста сюда не поместится." : `Осталось ${charCountText(left)}`;
}

/** Сколько хостов ссылок называть в предпросмотре, остальные считаются числом. */
export const PREVIEW_HOSTS_MAX = 3;

/**
 * Хосты ссылок текста задания по порядку, без повторов и без `www.`. Берутся из
 * того же `linkify`, что рисует ссылки ученику: адрес без `https://` останется у
 * него текстом, и здесь его тоже нет.
 */
export function taskLinkHosts(taskText: string): string[] {
  const hosts = linkify(taskText).flatMap((piece) =>
    piece.kind === "link" ? [hostAsTyped(piece.text)] : [],
  );
  return [...new Set(hosts)];
}

/**
 * Хост так, как его набрал преподаватель. ⚠️ Не `new URL().hostname`: тот
 * отдаёт punycode, и «решуогэ.рф» в предпросмотре стал бы «xn--...xn--p1ai».
 */
function hostAsTyped(text: string): string {
  const rest = text.replace(/^https?:\/\//i, "");
  const bracketed = /^\[[^\]]*\]/.exec(rest);
  if (bracketed !== null) return bracketed[0];
  return (rest.split(/[/\\?#:]/, 1)[0] ?? "").toLowerCase().replace(/^www\./, "");
}

/**
 * Строка предпросмотра про материал. `null`, если материала нет вовсе.
 * Счёт файлов это описание того, что уйдёт ученику, а не работы
 * преподавателя (5.10 CLAUDE.md не задето).
 *
 * Ссылки названы хостами (Р10 плана): это проверка до «Задать», что адрес
 * распознан и у ученика откроется нажатием.
 */
export function materialPreviewLine(args: {
  taskText: string;
  images: number;
  audio: number;
  video: number;
}): string | null {
  const text = hasTaskText(args.taskText);
  const parts: string[] = [];
  if (text) parts.push("текст задания");
  if (args.images > 0) parts.push(imageCountText(args.images));
  if (args.audio > 0) parts.push(audioCountText(args.audio));
  if (args.video > 0) parts.push(videoCountText(args.video));
  if (parts.length === 0) return null;
  const last = parts.pop();
  const what = parts.length === 0 ? last : `${parts.join(", ")} и ${last}`;
  const hosts = text ? taskLinkHosts(args.taskText) : [];
  if (hosts.length === 0) return `Материал: ${what}`;
  const named = hosts.slice(0, PREVIEW_HOSTS_MAX).join(", ");
  const rest = hosts.length - PREVIEW_HOSTS_MAX;
  return `Материал: ${what}; ссылки в тексте: ${rest > 0 ? `${named} и ещё ${rest}` : named}`;
}

/** Сколько в списке картинок, аудио и видео: для строки предпросмотра. */
export function materialCounts(items: readonly MaterialItem[]): {
  images: number;
  audio: number;
  video: number;
} {
  const ready = items.filter((it) => it.status === "ready" && it.fileId !== null);
  const media = (kind: "audio" | "video"): number =>
    ready.filter((it) => it.thumb.kind === "media" && it.thumb.fileKind === kind).length;
  const audio = media("audio");
  const video = media("video");
  return { images: ready.length - audio - video, audio, video };
}

/**
 * Отказ загрузки фото в форме выдачи. Читает ПРЕПОДАВАТЕЛЬ, поэтому это не
 * `homeworkPhotoErrorText`: там «у преподавателя закончилось место» написано
 * для ученика. Форматов не называем по той же причине, что у ученика: фото с
 * айфона в HEIC, и слово «WebP» не говорит, что делать.
 *
 * ⚠️ «Слишком большая» после нашего же ужатия была бы неправдой: дело в
 * канале, а не в снимке.
 */
export function materialUploadErrorText(code: string): string {
  switch (code) {
    case "quota_exceeded":
      return "Место для файлов закончилось, картинка не сохранилась.";
    case "source_too_large":
      return "Эту картинку не получилось подготовить. Попробуйте другую.";
    case "file_too_large":
      return "Картинка не доехала. Попробуйте ещё раз.";
    case "content_type_mismatch":
    case "image_broken":
      return "Эту картинку не получилось открыть. Попробуйте другую.";
    case "rate_limited":
      return "Слишком быстро. Подождите минуту и попробуйте ещё раз.";
    default:
      return "Картинка не загрузилась. Проверьте связь и попробуйте ещё раз.";
  }
}
